import React, { useMemo } from "react";
import { diffWordsWithSpace } from "diff";

/**
 * Word-level diff for enterprise-grade before/after view.
 * Additions in emerald, removals in rose with strike-through.
 */
const kindOf = (p) => (p.added ? "add" : p.removed ? "del" : "eq");

const Segment = ({ part, idx }) => {
  const k = kindOf(part);
  const key = `${k}-${idx}-${part.value.length}`;
  if (k === "add") {
    return (
      <span key={key} className="bg-emerald-100 text-emerald-900 rounded-sm px-0.5">
        {part.value}
      </span>
    );
  }
  if (k === "del") {
    return (
      <span key={key} className="bg-rose-100 text-rose-900 line-through decoration-rose-600/70 rounded-sm px-0.5">
        {part.value}
      </span>
    );
  }
  return (
    <span key={key} className="text-foreground/80">
      {part.value}
    </span>
  );
};

export default function WordDiff({ existing = "", proposed = "" }) {
  const parts = useMemo(() => diffWordsWithSpace(existing, proposed), [existing, proposed]);
  const adds = parts.filter((p) => p.added).length;
  const dels = parts.filter((p) => p.removed).length;

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden" data-testid="word-diff">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/80 bg-muted/30">
        <span className="tracked-caps">Inline diff · existing → proposed</span>
        <span className="font-mono text-[11px] text-muted-foreground">
          <span className="text-emerald-700">+{adds}</span>{" "}
          <span className="text-rose-700">−{dels}</span>
        </span>
      </div>
      <pre className="p-4 font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words">
        {parts.map((p, i) => (
          <Segment key={`${kindOf(p)}-${i}-${p.value.length}`} part={p} idx={i} />
        ))}
      </pre>
    </div>
  );
}
